import { useEffect } from 'react';
import { builderDispatch, WebUI, PrismUI } from '../../_utils/ui';
import {
  usePrintsUiStore,
  usePrintsConfigStore,
  usePrintsOrderStore,
} from '../stores';
import preloadImage from '../../_utils/image-processing';

/**
 * A non-visual component to manage the photo upload process
 * and bridge the legacy uploader with Zustand store.
 */
const UploadManager = () => {
  const {
    uploadBegun: uiUploadBegun,
    uploadProgress: uiUploadProgress,
    uploadAborted: uiUploadAborted,
    uploadEnded: uiUploadEnded,
  } = usePrintsUiStore((state) => ({
    uploadBegun: state.uploadBegun,
    uploadProgress: state.uploadProgress,
    uploadAborted: state.uploadAborted,
    uploadEnded: state.uploadEnded,
  }));

  const getPrintSizeKeyForNewUpload = usePrintsConfigStore(
    (state) => state.getters.getPrintSizeKeyForNewUpload,
  );
  const printsBuilderBlurhashDisabled = usePrintsConfigStore(
    (state) => state.features.printsBuilderBlurhashDisabled,
  );

  const {
    uploadBegun: orderUploadBegun,
    addUploadedPhoto,
    addPendingPhoto,
    confirmPhotoThumbLoaded,
    markPhotoThumbFailed,
    uploadFinished,
    debouncedSave,
    removeIncompleteUploads,
  } = usePrintsOrderStore((state) => ({
    uploadBegun: state.uploadBegun,
    addUploadedPhoto: state.addUploadedPhoto,
    addPendingPhoto: state.addPendingPhoto,
    confirmPhotoThumbLoaded: state.confirmPhotoThumbLoaded,
    markPhotoThumbFailed: state.markPhotoThumbFailed,
    uploadFinished: state.uploadFinished,
    debouncedSave: state.debouncedSave,
    removeIncompleteUploads: state.removeIncompleteUploads,
  }));

  useEffect(() => {
    let progressPromises = [];
    // Identifies the batch in flight. UPLOAD_DONE is handled asynchronously, so
    // a slow batch can settle after a newer one has begun; its handler must not
    // then reconcile or clear state that belongs to the newer batch.
    let batchGeneration = 0;

    const handleUploadBegun = (total) => {
      batchGeneration += 1;
      progressPromises = [];
      uiUploadBegun(total);
      orderUploadBegun();
    };

    const handleUploadProgress = (photo) => {
      const sizeKey = getPrintSizeKeyForNewUpload();

      // Blurhash enabled photos get added to state and displayed immediately where as
      // standard photos get added when thumbnail loads

      // Blurhash enabled: add the placeholder
      if (photo.blurhash && !printsBuilderBlurhashDisabled) {
        // Omit thumb_url so the component renders in blurhash only state while preloadImage runs.
        // confirmPhotoThumbLoaded will set the final blob URL once ready.
        // eslint-disable-next-line no-unused-vars
        const { thumb_url: _thumbUrl, ...photoWithoutThumb } = photo;
        const clientUuid = addPendingPhoto({ ...photoWithoutThumb, sizeKey });

        // decrement the loading counter
        uiUploadProgress();

        // trigger thumbnail load
        const promise = preloadImage(photo)
          .then((thumbUrl) => {
            confirmPhotoThumbLoaded({ clientUuid, thumbUrl });
          })
          .catch((error) => {
            console.error('Error preloading image for blurhash photo.', {
              photo,
              error,
            });
            // The upload itself succeeded, only the preview is missing, so keep the
            // photo and let it render its blurhash with the unavailable badge.
            markPhotoThumbFailed({ clientUuid });
          });

        progressPromises.push(promise);
        return;
      }

      // Standard photo fallback path
      const promise = preloadImage(photo)
        .then((thumbUrl) => {
          addUploadedPhoto({
            ...photo,
            // eslint-disable-next-line camelcase
            thumb_url: thumbUrl, // assign the url that was successfully fetched as thumb_url
            uploaded: true,
            sizeKey,
          });
        })
        .catch((error) => {
          console.error('Error preloading image, adding without thumbnail.', {
            photo,
            error,
          });
          // The upload itself succeeded, only the preview is missing, so keep the photo
          // and mark it uploaded. Edit is gated on thumbnailFailed, not on this.
          addUploadedPhoto({
            ...photo,
            uploaded: true,
            thumbnailFailed: true,
            sizeKey,
          });
        })
        .finally(() => {
          uiUploadProgress();
        });

      progressPromises.push(promise);
    };

    // Notify of upload complete when all photo thumbnail promises have completed and all photos in state
    const handleUploadDone = async (payload) => {
      const generation = batchGeneration;

      // Or if there are any errors then re-open the image loader
      if (payload.number_of_errors) {
        builderDispatch.delayedDispatch({
          type: WebUI.UPLOAD_SHOW_FAILURES,
          message: payload.message,
        });
      }

      try {
        await Promise.all(progressPromises);
      } catch (e) {
        console.error('One or more uploads failed to process.', e);
      } finally {
        if (generation === batchGeneration) {
          removeIncompleteUploads(); // This will remove photos that have not managed to have a thumbnail loaded
          uiUploadEnded(); // The batch is over, so stop showing loading placeholders
          uploadFinished();
        } else {
          // A newer batch owns the duplicate-detection baseline that
          // uploadFinished() would reconcile against and consume, so only the
          // autosave is safe to run for this one. The newer batch's own
          // UPLOAD_DONE reconciles for both.
          debouncedSave();
        }
        builderDispatch.delayedDispatch({ type: PrismUI.IMAGES_UPDATED });
      }
    };

    // Register listeners for the legacy uploader events
    const token = builderDispatch.register((payload) => {
      switch (payload.type) {
        case WebUI.UPLOAD_BEGUN:
          handleUploadBegun(payload.number_of_files);
          break;
        case WebUI.UPLOAD_PROGRESS:
          handleUploadProgress(payload.data);
          break;
        case WebUI.UPLOAD_DONE:
          handleUploadDone(payload);
          break;
        case WebUI.UPLOAD_ABORTED:
          uiUploadAborted();
          removeIncompleteUploads();
          break;
      }
    });

    return () => {
      builderDispatch.unregister(token);
    };
  }, [
    uiUploadBegun,
    uiUploadProgress,
    uiUploadAborted,
    uiUploadEnded,
    orderUploadBegun,
    addUploadedPhoto,
    addPendingPhoto,
    confirmPhotoThumbLoaded,
    markPhotoThumbFailed,
    uploadFinished,
    debouncedSave,
    removeIncompleteUploads,
    getPrintSizeKeyForNewUpload,
    printsBuilderBlurhashDisabled,
  ]);

  return null;
};

export default UploadManager;
